import { ShadowSettingKey, TimerSettings } from '@lessonup/pins-shared';
import {
  borderRadius,
  Button,
  color,
  IconPause,
  IconPlayFill,
  IconReset,
  styled,
  TextElement,
  useTranslation,
} from '@lessonup/ui-components';
import React from 'react';
import { computeShadowFilter } from '../../utils';
import { StateType as TimerState } from '../../utils/timer/Timer';

export const timerDefaultBorderColor = '#14171f33';

export interface TimerPinComponentProps {
  timerState: TimerState;
  secondsRemaining: number;
  settings?: TimerSettings;
  onAddTime?(): void;
  onReset?(): void;
  onPlayPause?(): void;
}

export const TimerPinComponent: React.FC<TimerPinComponentProps> = ({
  timerState,
  secondsRemaining,
  settings,
  onAddTime,
  onReset,
  onPlayPause,
}) => {
  const { t } = useTranslation('timerPinComponent');
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = Math.ceil(secondsRemaining % 60);

  return (
    <TimerContainer
      timerState={timerState}
      borderSize={settings?.borderSize}
      borderColor={settings?.borderColor || timerDefaultBorderColor}
      shadow={settings?.shadow}
    >
      <TimerItems>
        {minutes > 0 && (
          <TimerItem>
            <TextElement textStyle="heading" size="medium">
              {minutes}
            </TextElement>
            <StyledBodyText textStyle="bodyText" size="small">
              {t('minute', { count: minutes })}
            </StyledBodyText>
          </TimerItem>
        )}
        <TimerItem>
          <TextElement textStyle="heading" size="medium">
            {seconds.toString().padStart(2, '0')}
          </TextElement>
          <StyledBodyText textStyle="bodyText" size="small">
            {t('second', { count: seconds })}
          </StyledBodyText>
        </TimerItem>
      </TimerItems>
      <TimerControls>
        {timerState != 'idle' && (
          <ResetButton
            buttonType="neutral"
            buttonShape="rect"
            resizing="fixed"
            showStroke={true}
            iconStart={<IconReset />}
            onClick={onReset}
            size="small"
          />
        )}
        {timerState === 'ended' ? (
          <EndedText textStyle="bodyText" size="small">
            {t('ended')}
          </EndedText>
        ) : (
          <Button
            buttonType={timerState === 'idle' ? 'secondary' : 'neutral'}
            buttonShape="rect"
            iconStart={timerState === 'running' ? <IconPause /> : <IconPlayFill />}
            resizing="fixed"
            showStroke={true}
            onClick={onPlayPause}
            size="small"
          />
        )}
        <AddButton
          buttonType="neutral"
          buttonShape="rect"
          resizing="fixed"
          showStroke={true}
          onClick={onAddTime}
          size="small"
        >
          {t('add30s')}
        </AddButton>
      </TimerControls>
    </TimerContainer>
  );
};

const TimerContainer = styled.div<{
  timerState: TimerState;
  shadow?: ShadowSettingKey;
  borderSize?: number;
  borderColor: string;
}>`
  padding: 0.5rem;
  gap: 0.5rem;
  width: 100%;
  height: 100%;
  user-select: none;
  display: flex;
  flex-direction: column;
  border-width: ${({ borderSize }) => borderSize}px;
  border-style: solid;
  border-color: ${({ borderColor }) => borderColor};
  border-radius: ${borderRadius.rounded16};
  background-color: ${({ timerState }) => {
    if (timerState === 'paused') {
      return color.message.warning.fill;
    }
    if (timerState === 'ended') {
      return color.message.negative.fill;
    }
    return color.neutralNew.white.fill;
  }};
  ${(props) => props.shadow && `filter: drop-shadow(${computeShadowFilter(props.shadow)});`}
`;

const TimerItems = styled.div`
  display: flex;
  flex: 5 0 0;
  gap: 0.25rem;
`;

const TimerItem = styled.div`
  display: flex;
  background-color: ${color.hierarchy.tertiary.fillSubjacent};
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;
  border-radius: ${borderRadius.rounded8};
`;

const StyledBodyText = styled(TextElement)`
  color: ${color.additional.disabled.text};
`;

const EndedText = styled(TextElement)`
  color: ${color.additional.disabled.text};
  flex: 4 0 0;
  text-align: center;
`;

const TimerControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: 0.5rem;
  flex: 1 0 0;
`;

const AddButton = styled(Button)`
  flex: 2 0 0;
`;

const ResetButton = styled(Button)`
  flex: 1 0 0;
`;
